import { useCallback } from 'react';
import {
  approveBudget,
  createBudget,
  IApprovalPayload,
  rejectBudget,
  uploadBudgetFiles,
} from '../../../../api/fetchBudget';
import { BudgetStatusEnum } from '../../../../enums/budgetStatus';
import { BudgetRecipientEnum, IBudgetApiPayload, IBudgetFormData, IFileItem } from '../types';

interface IUseActionsParams {
  budgetId: string;
  occurrenceId: string;
  onSuccess?: () => void;
  username?: string;
}

interface IHandleCreateParams {
  buildApiPayload: () => IBudgetApiPayload;
  uploadFilesIfNeeded: (action: string, budgetIdToUse?: string) => Promise<boolean>;
  validateCounterProposalInput: () => boolean;
  onSuccess?: (createdBudgetId?: string) => void;
}

interface IHandleEditParams {
  status: BudgetStatusEnum | undefined;
  profile: BudgetRecipientEnum | undefined;
  orcamentoStatus: BudgetStatusEnum | undefined;
  initialData?: IBudgetFormData;
  buildApiPayload: () => IBudgetApiPayload;
  uploadFilesIfNeeded: (action: string, budgetIdToUse?: string) => Promise<boolean>;
  validateCounterProposalInput: () => boolean;
  onSuccess?: () => void;
}

interface IHandleRejectAndSendParams {
  rejectionObservation: string;
  files: IFileItem[];
  initialData?: IBudgetFormData;
  uploadFiles: (filesToUpload: File[], budgetIdToUse?: string) => Promise<boolean>;
  profile?: BudgetRecipientEnum;
  buildApiPayload?: () => IBudgetApiPayload;
}

interface IHandleFactoryApproveParams {
  initialData?: IBudgetFormData;
  buildApiPayload: () => IBudgetApiPayload;
  uploadFilesIfNeeded: (action: string, budgetIdToUse?: string) => Promise<boolean>;
  onSuccess?: () => void;
  username?: string;
}

interface IHandleFactoryRejectParams {
  rejectionObservation?: string;
  files: IFileItem[];
  initialData?: IBudgetFormData;
  uploadFiles: (filesToUpload: File[], budgetIdToUse?: string) => Promise<boolean>;
  buildApiPayload?: () => IBudgetApiPayload;
  username?: string;
}

export const useActions = (params: IUseActionsParams) => {
  const { budgetId, occurrenceId, onSuccess, username } = params;

  const uploadFiles = useCallback(
    async (filesToUpload: File[], budgetIdToUse?: string): Promise<boolean> => {
      if (filesToUpload.length === 0) {
        return true;
      }

      const budgetIdForUpload = budgetIdToUse || budgetId;

      const uploadSuccess = await uploadBudgetFiles(budgetIdForUpload, filesToUpload);

      if (!uploadSuccess) {
        return false;
      }

      return true;
    },
    [budgetId],
  );

  const handleCreate = useCallback(
    async (params: IHandleCreateParams) => {
      try {
        const {
          buildApiPayload,
          uploadFilesIfNeeded,
          validateCounterProposalInput,
          onSuccess: onCreateSuccess,
        } = params;

        if (!validateCounterProposalInput()) {
          return;
        }

        const apiPayload = buildApiPayload();

        const result = await createBudget(apiPayload);

        if (result.error) {
          alert(`Erro ao criar orçamento: ${result.error}`);
          return;
        }

        // A API retorna { id: "..." } no status 201
        const createdBudgetId = result.data?.id || result.data?.uuid || budgetId;

        const uploaded = await uploadFilesIfNeeded('Enviar orçamento', createdBudgetId);

        if (onCreateSuccess) {
          onCreateSuccess(createdBudgetId);
        } else if (onSuccess) {
          onSuccess();
        }
      } catch (error) {}
    },
    [budgetId],
  );

  const handleApprove = useCallback(
    async (
      params: IHandleFactoryApproveParams,
      role: 'consultant' | 'factory',
    ) => {
      try {
        const { initialData, buildApiPayload, uploadFilesIfNeeded, onSuccess: onApproveSuccess, username } = params;

        if (!initialData?.versionNumber) {
          alert('Erro: versão do orçamento não encontrada');
          return;
        }

        const apiPayload = buildApiPayload();
        const versionNumber = initialData.versionNumber;
        const approvalPayload: IApprovalPayload = {
          services: apiPayload.services,
          subtotal: apiPayload.subtotal,
          discount: apiPayload.discount,
          netTotal: apiPayload.netTotal,
          observation: initialData?.observation || undefined,
          counterProposalObservation: initialData?.counterProposalObs,
          counterProposalValue: initialData?.counterProposalValue,
        };

        const result = await approveBudget(budgetId, versionNumber, role, approvalPayload);

        if (result.error) {
          alert(`Erro ao aprovar orçamento: ${result.error}`);
          return;
        }

        await uploadFilesIfNeeded('Aprovar orçamento');

        if (onApproveSuccess) {
          onApproveSuccess();
        } else if (onSuccess) {
          onSuccess();
        }
      } catch (error) {}
    },
    [budgetId, occurrenceId, onSuccess],
  );

  const handleEdit = useCallback(
    async (params: IHandleEditParams) => {
      try {
        const {
          profile,
          initialData,
          buildApiPayload,
          uploadFilesIfNeeded,
          validateCounterProposalInput,
          onSuccess: onEditSuccess,
        } = params;

        if (!validateCounterProposalInput()) {
          return;
        }

        const canApproveRejected = 
          initialData?.versionNumber !== undefined &&
          (profile === BudgetRecipientEnum.CONSULTANT || profile === BudgetRecipientEnum.TOWER);

        if (canApproveRejected && initialData?.versionNumber) {
          const role: 'consultant' | 'factory' = 
            profile === BudgetRecipientEnum.CONSULTANT ? 'consultant' : 'factory';

          await handleApprove(
            {
              initialData,
              buildApiPayload,
              uploadFilesIfNeeded,
              onSuccess: onEditSuccess || onSuccess,
              username,
            },
            role,
          );

          return;
        }

        const uploaded = await uploadFilesIfNeeded('Enviar alterações');
        if (!uploaded) {
          return;
        }
      } catch (error) {}
    },
    [budgetId, occurrenceId, onSuccess, handleApprove],
  );

  const handleFactoryApprove = useCallback(
    async (params: IHandleFactoryApproveParams) => {
      return handleApprove(params, 'factory');
    },
    [handleApprove],
  );

  const handleReject = useCallback(
    async (
      params: IHandleFactoryRejectParams,
      role: 'factory' | 'consultant',
      requireObservation: boolean = false,
    ) => {
      try {
        const { rejectionObservation, files, initialData, uploadFiles: uploadFilesAction, username } = params;

        if (requireObservation && (!rejectionObservation || rejectionObservation.trim() === '')) {
          alert('A observação é obrigatória ao recusar o orçamento.');
          return;
        }

        if (!initialData?.versionNumber) {
          alert('Erro: versão do orçamento não encontrada');
          return;
        }

        const versionNumber = initialData.versionNumber;

        const result = await rejectBudget({
          budgetId,
          versionNumber,
          role,
          rejectionObservation,
          counterProposalObservation: initialData?.counterProposalObs,
          counterProposalValue: initialData?.counterProposalValue,
        });

        if (result.error) {
          alert(`Erro ao recusar orçamento: ${result.error}`);
          return;
        }

        const filesToUpload: File[] = files
          .filter((fileItem) => fileItem.file !== undefined)
          .map((fileItem) => fileItem.file as File);

        if (filesToUpload.length > 0) {
          await uploadFilesAction(filesToUpload, budgetId);
        }

        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {}
    },
    [budgetId, occurrenceId, onSuccess],
  );

  const handleRejectWithServices = useCallback(
    async (
      params: IHandleFactoryRejectParams & {
        services: IApprovalPayload['services'];
        proposalServices?: IApprovalPayload['proposalServices'];
        subtotal: number;
        discount: number;
        netTotal: number;
      },
      role: 'factory' | 'consultant',
      requireObservation: boolean = false,
    ) => {
      try {
        const { rejectionObservation, files, initialData, uploadFiles: uploadFilesAction, services, proposalServices, subtotal, discount, netTotal } = params;

        if (requireObservation && (!rejectionObservation || rejectionObservation.trim() === '')) {
          alert('A observação é obrigatória ao recusar o orçamento.');
          return;
        }

        if (!initialData?.versionNumber) {
          alert('Erro: versão do orçamento não encontrada');
          return;
        }

        const versionNumber = initialData.versionNumber;

        const finalSubtotal = subtotal !== undefined && subtotal !== null ? subtotal : 0;
        const finalDiscount = discount !== undefined && discount !== null ? discount : 0;
        const finalNetTotal = netTotal !== undefined && netTotal !== null ? netTotal : 0;

        const result = await rejectBudget({
          budgetId,
          versionNumber,
          role,
          rejectionObservation,
          services,
          proposalServices,
          subtotal: finalSubtotal,
          discount: finalDiscount,
          netTotal: finalNetTotal,
          counterProposalObservation: initialData?.counterProposalObs,
          counterProposalValue: initialData?.counterProposalValue,
        });

        if (result.error) {
          alert(`Erro ao recusar orçamento: ${result.error}`);
          return;
        }

        const filesToUpload: File[] = files
          .filter((fileItem) => fileItem.file !== undefined)
          .map((fileItem) => fileItem.file as File);

        if (filesToUpload.length > 0) {
          await uploadFilesAction(filesToUpload, budgetId);
        }

        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {}
    },
    [budgetId, occurrenceId, onSuccess],
  );

  const handleRejectAndSend = useCallback(
    async (params: IHandleRejectAndSendParams) => {
      if (params.profile === BudgetRecipientEnum.MANAGER) {
        alert('Manager deve usar a função específica de rejeição');
        return;
      }
      
      let role: 'factory' | 'consultant' = 'factory';
      
      if (params.profile === BudgetRecipientEnum.TOWER) {
        role = 'factory';
      } else if (params.profile === BudgetRecipientEnum.CONSULTANT) {
        role = 'consultant';
      }

      if (params.buildApiPayload) {
        const apiPayload = params.buildApiPayload();
        return handleRejectWithServices(
          {
            rejectionObservation: params.rejectionObservation,
            files: params.files,
            initialData: params.initialData,
            uploadFiles: params.uploadFiles,
            services: apiPayload.services,
            proposalServices: apiPayload.proposalServices,
            subtotal: apiPayload.subtotal,
            discount: apiPayload.discount,
            netTotal: apiPayload.netTotal,
          },
          role,
          true,
        );
      }

      return handleReject(
        {
          rejectionObservation: params.rejectionObservation,
          files: params.files,
          initialData: params.initialData,
          uploadFiles: params.uploadFiles,
        },
        role,
        true,
      );
    },
    [handleReject, handleRejectWithServices],
  );

  const handleFactoryReject = useCallback(
    async (params: IHandleFactoryRejectParams) => {
      if (params.buildApiPayload) {
        const apiPayload = params.buildApiPayload();
        return handleRejectWithServices(
          {
            rejectionObservation: params.rejectionObservation || '',
            files: params.files,
            initialData: params.initialData,
            uploadFiles: params.uploadFiles,
            services: apiPayload.services,
            proposalServices: apiPayload.proposalServices,
            subtotal: apiPayload.subtotal,
            discount: apiPayload.discount,
            netTotal: apiPayload.netTotal,
          },
          'factory',
          false,
        );
      }
      return handleReject(params, 'factory', false);
    },
    [handleReject, handleRejectWithServices],
  );

  return {
    uploadFiles,
    handleCreate,
    handleEdit,
    handleRejectAndSend,
    handleFactoryApprove,
    handleFactoryReject,
  };
};
